import React, { useState, useEffect, useRef, useContext, useMemo } from 'react'
import { Link, useLocation, useHistory } from 'react-router-dom'
import {FormattedDate, FormattedMessage, useIntl} from 'react-intl'
import Icon from 'components/Icon'
import { Button } from '@arnsbogroup/react-voicearchive'
import { Flex as Loading } from 'components/Loading'

import { BrandContext } from 'CustomerApp'
import OrderRow from './components/OrderRow'
import OrderHeadRow from './components/OrderHeadRow'
import OrderDetailsPane from './components/OrderDetailsPane'
import Filters from './components/Filters'
import messages from './messages'
import ResizableSidebar from 'components/ResizableSidebar'

const CustomerOrdersPage = (props) => {
  const tableRef = useRef(null)
  const location = useLocation()
  const history = useHistory()
  const { brand } = useContext(BrandContext)
  const { formatMessage } = useIntl()

  const params = new URLSearchParams(location.search.substr(1))
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState([])
  const [orders, setOrders] = useState([])
  const [columns, setColumn] = useState([])
  const [showFilters, setShowFilters] = useState(false)

  const [filters, setFilters] = useState(() => {
    let stored = {}

    if (window.localStorage) {
      stored = JSON.parse(localStorage.getItem('voiceflow.filters') || '{}')
    }

    return {
      customerId: stored.customerId || [],
      contactId: stored.contactId || [],
      stage: stored.stage || null,
      template: stored.template || null
    }
  })

  const onFilterChange = (nextFilters) => {
    setFilters(nextFilters)

    if (window.localStorage) {
      localStorage.setItem('voiceflow.filters', JSON.stringify(nextFilters))
    }
  }

  const onFilterClear = () => {
    setFilters({ customerId: [], contactId: [], stage: null, template: null })

    if (window.localStorage) {
      localStorage.removeItem('voiceflow.filters')
    }
  }

  const [page, setPage] = useState(params.get('page') || 1)
  const [sortBy, setSortBy] = useState(params.get('sortBy') || 'crdate')
  const [sortDirection, setSortDirection] = useState(params.get('sortDirection') || 'DESC')
  const [query, setQuery] = useState(params.get('query') || '')
  const [orderDetailsPaneId, setOrderDetailsPaneId] = useState(params.get('id'))

  useEffect(() => {
    fetch(`/rest/v2/customer/order-attributes`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((columns) => setColumn(columns.filter(({ show }) => show)))
  }, [])


  useEffect(() => {
    setLoading(true)
    let params = new URLSearchParams()

    params.append('sortDirection', sortDirection)
    params.append('sortBy', sortBy)
    params.append('pageSize', 50)
    params.append('page', page)
    params.append('query', query)

    Object.entries(filters).map(([key, value]) => {
      if (Array.isArray(value)) {
        value.map((v) => params.append(`${key}[]`, encodeURIComponent(v)))
      } else if (!!value) {
        params.append(key, encodeURIComponent(value))
      }
    })

    history.push(location.pathname + '?' + params.toString())

    fetch(`/rest/v2/order?${params.toString()}`, {
      credentials: 'same-origin',
      method: 'GET',
    })
      .then((response) => response.json())
      .then(({ success, meta, data, errors }) => {
        setLoading(false)

        if (success) {
          setOrders(data)
          setMeta(meta)
        } else {
          window.alert(errors.join(' - '))
        }

        if (tableRef.current) {
          tableRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      })
      .catch((err) => {
        window.alert(err)
      })
  }, [sortBy, sortDirection, page, query, filters])

  const onSort = (key) => () => {
    if (sortBy !== key) {
      setSortBy(key)
    } else {
      setSortDirection(sortDirection === 'DESC' ? 'ASC' : 'DESC')
    }

    setPage(1)
  }

  const selectedOrder = useMemo(() => (
    orders.find(({ uid }) => uid === orderDetailsPaneId)
  ), [orderDetailsPaneId, orders])

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-none flex justify-between items-center">
        <div className="grow px-3 xl:px-6 py-2 xl:py-4 flex items-center space-x-2 xl:space-x-4">
          <Icon icon="search" />

          <input
            defaultValue={query}
            onBlur={({ target: { value } }) => setQuery(value)}
            onChange={({ target: { value } }) => {
              if (value === '') {
                setQuery(value)
              }
            }}
            onKeyDown={({ key, target: { value } }) => {
              if (key === 'Enter' || value === '') {
                setQuery(value)
              }
            }}
            className="w-1/3 py-2 px-4 w-full text-sm"
            placeholder={formatMessage(messages.searchPlaceholder)}
          />
        </div>
        <div className="flex items-center space-x-3 xl:space-x-6 pr-3">
          <Button 
            onClick={() => setShowFilters(!showFilters)}
            icon={['fa', showFilters ? 'arrow-up' : 'arrow-down']}
            variant="inline"
          >
            Filters
          </Button>
          { selectedOrder && brand.voiceflow && selectedOrder.order_template_uid &&
            <>
              <Link to={`/orders/${selectedOrder.uid}`}>
                <Button 
                  variant="outline" 
                  title={formatMessage(messages.edit)}
                  icon={['fa', 'pencil']}
                >
                  <FormattedMessage {...messages.edit}/>
                </Button>
              </Link>
            </>
          }

          { brand.voiceflow &&
            <Link to="/orders/new">
              <Button icon={['fa', 'plus']}>
                <FormattedMessage {...messages.newOrder}/>
              </Button>
            </Link>
          }
        </div>
      </div>

      { showFilters &&
        <div className="w-full">
          <Filters onChange={onFilterChange} filters={filters} onClear={onFilterClear} />
        </div>
      }

      <div className="grow flex w-full overflow-hidden" ref={tableRef}>
        <div className={`h-full overflow-y-scroll flex flex-col ${orderDetailsPaneId ? 'w-2/3 2xl:w-3/4' : 'w-full'}`}>
          <table className="overflow-scroll relative table-fixed mw-full">
            <thead>
              <OrderHeadRow
                columns={columns}
                sortBy={sortBy}
                onSort={onSort}
                sortDirection={sortDirection}
              />
            </thead>
            { !loading &&
              <tbody>
                { orders.map((order) => (
                  <OrderRow
                    key={order.uid}
                    order={order}
                    columns={columns}
                    onClick={() => setOrderDetailsPaneId(order.uid)}
                    isSelected={order.uid == orderDetailsPaneId}
                  />
                )) }
              </tbody>
            }
          </table>

          { loading &&
            <Loading />
          }

          { !orders.length && !loading &&
            <Loading>
              { query.length ? (
                <span><FormattedMessage{...messages.noResultsQuery}/>"{ query }"</span>
              ) : (
                <span><FormattedMessage{...messages.noResultsFilters}/></span>
              ) }
            </Loading>
          }
        </div>

        { selectedOrder &&
          <ResizableSidebar>
            <OrderDetailsPane
              order={selectedOrder}
              onHide={() => setOrderDetailsPaneId(null)}
            />
          </ResizableSidebar>
        }
      </div>

      { meta &&
        <div className="w-full flex justify-between items-center px-3 xl:px-6 text-sm font-medium text-gray-600 uppercase tracking-wider group cursor-pointer hover:bg-gray-100 border-t border-gray-100">
          <div>{ (meta.currentPage * 50) - 50 } – { meta.currentPage * 50 } <FormattedMessage {...messages.of}/> { meta.total }</div>

          <div className="grow py-3 xl:py-6 text-center text-white group-hover:text-gray-400" onClick={() => setPage((page) => parseInt(page) + 1)}>
            Load more
          </div>

          <div>
            { page > 1 &&
              <span className="px-6 cursor-pointer hover:text-primary" onClick={() => setPage((page) => parseInt(page, 10) - 1)}>
                <Icon icon="arrow-left" />
              </span>
            }

            <span>{ meta.currentPage } <FormattedMessage {...messages.of}/> { meta.pages }</span>

            { page < meta.pages &&
              <span className="pl-6 cursor-pointer hover:text-primary" onClick={() => setPage((page) => parseInt(page, 10) + 1)}>
                <Icon icon="arrow-right" />
              </span>
            }
          </div>
        </div>
      }
    </div>
  )
}

export default CustomerOrdersPage
